'use client'

import classNames from 'classnames'
import moment from 'moment-timezone'
import { FunctionComponent, ReactNode, useContext, useMemo } from 'react'
import { useSeller } from '@/app/_providers/SellerProvider/SellerProvider'
import { useOurStore } from '@/app/_providers/StoreProvider'
import LoginContext from '@/app/_providers/LoginProvider/LoginContext'
import StringToReactNode from '@/app/_components/StringToReactNode/StringToReactNode'
import TXT from '@/messages'
import styles from './MembershipPerks.module.css'
import HorizontalSquiggly from '@/app/_components/HorizontalSquiggly/HorizontalSquiggly'
import SimpleCollapsible from '../SimpleCollapsible/SimpleCollapsible'
import { AdmissionType } from '@/types/general.types'
import { getCartSubtotal } from '@/utils/cart.utils'
import useApplyPerk from '@/hooks/useApplyPerk'
import { useCart } from '@/app/_providers/CartProvider/CartProvider'

const MembershipPerks: FunctionComponent = () => {
  const admissionType = useOurStore((state) => state.admissionType)
  const { cart } = useCart()
  const { timezone } = useSeller()
  const { membershipDetails, membershipMeta, membership, user } = useContext(LoginContext)

  const { loading: applyPerkLoading, applyPerk } = useApplyPerk()

  const nextPerksRemaining = useMemo(() => {
    if (!membershipMeta || !membershipMeta?.perksRemaining) {
      return 0
    }
    // calculate updated perks remaining value
    const freeTickets = cart?.appliedPerks?.freeTickets.length || 0
    return membershipMeta.perksRemaining - freeTickets
  }, [cart?.appliedPerks?.freeTickets.length, membershipMeta])

  if (
    !user
      || !membership
      || !cart
  ) {
    return null
  }

  const subtotalWithoutAddons = getCartSubtotal(cart, { withAddOns: false, withDiscount: true })
  const isDayPass = admissionType === AdmissionType.DayPass

  const renderMembershipDescription = (): ReactNode => {
    if (membership?.membershipDescription && !isDayPass) {
      return (
        <StringToReactNode incomingString={membership.membershipDescription} />
      )
    }

    if (isDayPass) {
      return (
        <span data-testid="perks-unavailable" className={styles.ErrorText}>
          {TXT.alerts.perkUnavailable(membership?.role)}
        </span>
      )
    }
    return null
  }

  return (
    <>
      <HorizontalSquiggly />
      <div className={styles.Title} data-testid="membership-role">
        {TXT.titles.components.membershipPerks(membership.role)}
      </div>
      {/* Membership description (from ticket_group) */}
      {renderMembershipDescription()}
      {/* Perks Remaining */}
      {membershipDetails?.hasCustomPerks && (
        <div data-testid="perks-remaining" className={styles.Container}>
          <div className={styles.SmallText}>
            <>
              {TXT.alerts.remainingTickets(
                nextPerksRemaining > 0 ? nextPerksRemaining : 'No',
              )}
            </>
          </div>
          {subtotalWithoutAddons !== 0 && nextPerksRemaining > 0 && !isDayPass && (
            <button
              data-testid="apply-perk-button"
              type="button"
              className={classNames(
                'btn-unstyled',
                styles.Button,
                { [styles.Disabled]: applyPerkLoading },
              )}
              onClick={() => applyPerk()}
              disabled={applyPerkLoading}
            >
              {applyPerkLoading ? TXT.buttons.applying : TXT.buttons.applyPerk}
            </button>
          )}
        </div>
      )}
      {/* Alumni Expiration */}
      {membershipDetails?.isAlumni && (
        <div
          data-testid="alumni-details"
          className={styles.SmallText}
        >
          <StringToReactNode incomingString={TXT.alerts.benefitsExpire(
            moment.tz(membership.validTo, timezone)
              .format('L'),
          )}
          />
        </div>
      )}
      {/* Annual Pass or All Locations Annual Pass (SUPREMIUM) details */}
      {(membershipDetails?.isAnnualPass || membershipDetails?.isAllLocationsAnnualPass) && (
        <div data-testid="annual-pass-details">
          <div className="separator light" />
          <div className={styles.MembershipDetailsWrapper}>
            <SimpleCollapsible header={<>{TXT.titles.components.membershipPerksPassDetails}</>}>
              <div className={classNames(styles.MembershipDetails, styles.SmallText)}>
                <StringToReactNode incomingString={TXT.alerts.benefitsExpire(
                  moment.tz(membership.validTo, timezone)
                    .format('L'),
                )}
                />
                <StringToReactNode incomingString={TXT.orderSummary.annualPassNumber(membership.passNumber)} />
                <ul className={styles.MembershipDetailsList} data-testid="purchase-limits">
                  {membership.purchaseLimits.map((item) => {
                    if (!item) return null
                    return (
                      <li key={item.key}>
                        {item.value}
                        {' x '}
                        {item.key}
                      </li>
                    )
                  })}
                </ul>
              </div>
            </SimpleCollapsible>
          </div>
        </div>
      )}
      <HorizontalSquiggly />
    </>
  )
}

export default MembershipPerks
